import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, Heart, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import SectionHeading from '../components/SectionHeading';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const skillOptions = [
  'Teaching / Tutoring',
  'Event Planning',
  'Fundraising',
  'Social Media',
  'Photography / Video',
  'Cooking / Food Preparation',
  'Transportation / Delivery',
  'Administrative / Office Work',
  'Healthcare / Medical',
  'Construction / Maintenance',
  'Translation',
  'Other'
];

const interestOptions = [
  'Orphan Support Program',
  'Food Bank Distribution',
  'Clothing Drives',
  'Education Programs',
  'Community Events',
  'Youth Mentoring',
  'Newcomer Assistance',
  'Friday Jummah Setup',
  'General Support'
];

const VolunteerPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    skills: '',
    availability: '',
    interests: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/volunteers`, formData);
      setIsSubmitted(true);
      toast.success('Thank you for volunteering!');
    } catch (error) {
      console.error('Error submitting volunteer form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-8"
          data-testid="volunteer-success"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Thank You!
          </h1>
          <p className="text-muted-foreground mb-6">
            Your volunteer application has been received. We'll be in touch soon with opportunities 
            that match your skills and interests.
          </p>
          <Button onClick={() => window.location.href = '/'} className="rounded-full">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Join Our Team
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Become a <span className="text-gradient">Volunteer</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Your time and skills can make a tremendous difference in the lives of those we serve. 
              Join our team of dedicated volunteers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 lg:py-32" data-testid="volunteer-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="volunteer-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      className="h-12 rounded-xl"
                      data-testid="volunteer-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(123) 456-7890"
                      className="h-12 rounded-xl"
                      data-testid="volunteer-phone"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 rounded-xl"
                    data-testid="volunteer-email"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skills">Skills & Expertise</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, skills: value })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="volunteer-skills">
                      <SelectValue placeholder="Select your primary skill" />
                    </SelectTrigger>
                    <SelectContent>
                      {skillOptions.map((skill) => (
                        <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availability">Availability</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, availability: value })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="volunteer-availability">
                      <SelectValue placeholder="When are you available?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekdays">Weekdays</SelectItem>
                      <SelectItem value="weekends">Weekends</SelectItem>
                      <SelectItem value="evenings">Evenings</SelectItem>
                      <SelectItem value="fridays">Friday Jummah Only</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                      <SelectItem value="events">Special Events Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interests">Areas of Interest</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, interests: value })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="volunteer-interests">
                      <SelectValue placeholder="Select your area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {interestOptions.map((interest) => (
                        <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-semibold"
                  data-testid="volunteer-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Users className="mr-2 h-5 w-5" />
                      Submit Application
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Info Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground mb-4">Why Volunteer?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Earn rewards from Allah by serving others</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Build meaningful connections in your community</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Develop new skills and experience</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Make a direct impact on families in need</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground mb-4">Current Needs</h3>
                <div className="space-y-3">
                  <div className="p-3 rounded-xl bg-primary/10 text-sm">
                    <span className="font-medium text-primary">Friday Jummah</span>
                    <p className="text-muted-foreground mt-1">Setup and cleanup assistance</p>
                  </div>
                  <div className="p-3 rounded-xl bg-secondary/10 text-sm">
                    <span className="font-medium text-secondary">Food Bank</span>
                    <p className="text-muted-foreground mt-1">Sorting and distribution volunteers</p>
                  </div>
                  <div className="p-3 rounded-xl bg-primary/10 text-sm">
                    <span className="font-medium text-primary">Education</span>
                    <p className="text-muted-foreground mt-1">Tutors for various subjects</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <blockquote className="text-lg italic text-foreground mb-3">
                  "The most beloved of people to Allah are those who are most beneficial to others."
                </blockquote>
                <p className="text-sm text-muted-foreground">— Prophet Muhammad (PBUH)</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
